/**
 * NetworkIndicator — compact pill showing real-time network health.
 *
 * Three states:
 *   • OK            — backend OK + WS open
 *   • Reconnexion…  — WS dropped (still pinging backend OK)
 *   • Hors ligne    — navigator.offline OR backend unreachable
 */
import React from "react";
import { Wifi, WifiOff, Loader2 } from "lucide-react";

export function NetworkIndicator({ online, backendReachable, wsStatus, compact = false }) {
  const offline = !online || !backendReachable;
  const reconnecting = !offline && (wsStatus === "reconnecting" || wsStatus === "connecting");

  let tone, label, Icon, pulse;
  if (offline) {
    tone = "bg-red-100 text-red-800 border-red-300";
    label = "Hors ligne";
    Icon = WifiOff;
    pulse = false;
  } else if (reconnecting) {
    tone = "bg-amber-100 text-amber-800 border-amber-300";
    label = "Reconnexion…";
    Icon = Loader2;
    pulse = true;
  } else {
    tone = "bg-green-100 text-green-800 border-green-200";
    label = "En ligne";
    Icon = Wifi;
    pulse = false;
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold tracking-wide ${tone}`}
      data-testid="network-indicator"
      data-state={offline ? "offline" : reconnecting ? "reconnecting" : "online"}
      title={`${label}${wsStatus ? ` · WS=${wsStatus}` : ""}`}
    >
      <Icon className={`w-3.5 h-3.5 ${pulse ? "animate-spin" : ""}`} />
      {!compact && <span>{label}</span>}
    </div>
  );
}

/**
 * Full-width offline banner — recommended for the kiosk so the customer
 * never sees a half-broken page.
 */
export function OfflineBanner({ online, backendReachable }) {
  if (online && backendReachable) return null;
  return (
    <div
      className="bg-red-50 border-b-2 border-red-300 text-red-900 px-4 py-3 flex items-center justify-center gap-3"
      data-testid="offline-banner"
      role="alert"
    >
      <WifiOff className="w-5 h-5 text-red-700" />
      <div className="text-sm font-semibold">
        Connexion serveur indisponible — veuillez patienter ou demander de l'aide à l'accueil.
      </div>
    </div>
  );
}
